// Sortierwahl (Agent 5B, Runde 5): Popover am Knopf „Sortieren“ der Bedienleiste, wie in Close mit Suche, Richtung und Feldern in Gruppen,
// dazu bis zu drei Sortierfelder (Close: eins). Klick auf ein Feld macht es zum ersten Sortierfeld, ein zweiter Klick kippt die Richtung.
import { useEffect, useMemo, useRef, useState } from "react";
import { Ausklapp } from "../Ausklapp";
import { Icon } from "../Icon";
import { feldTrifft, gleicheSortierung, gruppiere, sortEntfernen, sortRichtungSetzen, sortWaehlen, type SortEintrag } from "./BedienleisteLogik";
import type { ListenSortierung, ListenSpalte } from "./listenVertrag";
import "./Bedienleiste.css";

interface Props {
  anker: HTMLElement | null;
  offen: boolean;
  onSchliessen: () => void;
  /** wählbare Felder (nur sortierbare werden gezeigt) */
  felder: ListenSpalte[];
  /** eigene Wahl des Nutzers */
  sortierung: ListenSortierung[];
  /** Sortierung der Liste ohne eigene Wahl (Smart View: „Reihenfolge der Ansicht“), nur zur Anzeige */
  standard: ListenSortierung[];
  standardName: string;
  onSortierung: (s: ListenSortierung[]) => void;
  /** höchstens so viele Sortierfelder (Listen ohne Mehrfachsortierung: 1) */
  max: number;
  /** Hinweis unten, z. B. „Sortiert nur die 600 geladenen Treffer“ */
  hinweis?: string | null;
}

export function SortierWahl({ anker, offen, onSchliessen, felder, sortierung, standard, standardName, onSortierung, max, hinweis }: Props) {
  const [suche, setSuche] = useState("");
  const [richtung, setRichtung] = useState(false); // false = aufsteigend, für neu gewählte Felder
  const eingabe = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (offen) {
      setSuche("");
      window.setTimeout(() => eingabe.current?.focus(), 0);
    }
  }, [offen]);

  const sortierbar = useMemo(() => felder.filter((f) => f.sortierbar !== false), [felder]);
  const label = (key: string) => felder.find((f) => f.key === key)?.label ?? key;
  const treffer = useMemo(() => (suche.trim() ? sortierbar.filter((f) => feldTrifft(f, suche)) : sortierbar), [sortierbar, suche]);
  const gruppen = useMemo(() => gruppiere(treffer), [treffer]);
  const eigen = sortierung;

  const setzen = (s: SortEintrag[]) => onSortierung(s.slice(0, max));
  const ersetzt = standard.length > 0 && !eigen.length;

  return (
    <Ausklapp anker={anker} offen={offen} onSchliessen={onSchliessen} ausrichtung="end" breite={320} maxHoehe={520} rolle="dialog">
      <div className="bl-pop" role="dialog" aria-label="Sortierung">
        <label className="bl-pop__suche">
          <Icon name="suche" groesse={14} />
          <input ref={eingabe} value={suche} placeholder="Feld suchen …" aria-label="Sortierfeld suchen" onChange={(e) => setSuche(e.target.value)} />
        </label>
        <div className="bl-segment" role="group" aria-label="Richtung für das nächste Feld">
          <button type="button" aria-pressed={!richtung} onClick={() => setRichtung(false)}>
            <Icon name="pfeil-hoch" groesse={12} /> Aufsteigend
          </button>
          <button type="button" aria-pressed={richtung} onClick={() => setRichtung(true)}>
            <Icon name="pfeil-runter" groesse={12} /> Absteigend
          </button>
        </div>

        {eigen.length ? (
          <div className="bl-aktiv" aria-label="Aktive Sortierung">
            {eigen.map((s, i) => (
              <div key={s.key} className="bl-aktiv__zeile">
                <span className="bl-aktiv__nr">{eigen.length > 1 ? i + 1 : ""}</span>
                <span className="bl-aktiv__name" title={label(s.key)}>
                  {label(s.key)}
                </span>
                <button
                  type="button"
                  className="bl-aktiv__richtung"
                  title={s.ab ? "Absteigend, zum Umkehren klicken" : "Aufsteigend, zum Umkehren klicken"}
                  onClick={() => setzen(sortRichtungSetzen(eigen, s.key, !s.ab))}
                >
                  <Icon name={s.ab ? "pfeil-runter" : "pfeil-hoch"} groesse={12} />
                  {s.ab ? "Absteigend" : "Aufsteigend"}
                </button>
                <button type="button" className="bl-gezeigt__knopf" aria-label={`${label(s.key)} aus der Sortierung nehmen`} title="Aus der Sortierung nehmen" onClick={() => setzen(sortEntfernen(eigen, s.key))}>
                  <Icon name="schliessen" groesse={12} />
                </button>
              </div>
            ))}
          </div>
        ) : ersetzt ? (
          <div className="bl-aktiv bl-aktiv--standard">
            {standardName}: {standard.map((s) => `${s.ab ? "↓" : "↑"} ${label(s.key)}`).join(", ")}
          </div>
        ) : null}

        <div className="bl-pop__liste" role="listbox" aria-label="Sortierfelder">
          {gruppen.length === 0 ? <div className="bl-pop__leer">Kein Feld gefunden.</div> : null}
          {gruppen.map((g) => (
            <div key={g.gruppe}>
              {gruppen.length > 1 || suche ? <div className="bl-pop__ueberschrift">{g.gruppe}</div> : null}
              {g.felder.map((f) => {
                const s = eigen.find((x) => x.key === f.key);
                return (
                  <button
                    key={f.key}
                    type="button"
                    role="option"
                    aria-selected={!!s}
                    className={`bl-eintrag${s ? " bl-eintrag--an" : ""}`}
                    onClick={() => setzen(sortWaehlen(eigen, f.key, richtung, max))}
                  >
                    <span className="bl-eintrag__haken">{s ? <Icon name="haken" groesse={14} /> : null}</span>
                    <span className="bl-eintrag__text" title={f.label}>
                      {f.label}
                    </span>
                    {s ? (
                      <span className="bl-eintrag__rechts">
                        <Icon name={s.ab ? "pfeil-runter" : "pfeil-hoch"} groesse={12} />
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        {hinweis || eigen.length ? (
          <div className="bl-pop__fuss">
            {hinweis ? <span className="bl-pop__hinweis" style={{ border: 0, padding: 0 }}>{hinweis}</span> : null}
            {eigen.length && !gleicheSortierung(eigen, standard) ? (
              <button type="button" className="bl-pop__link" style={{ marginLeft: "auto" }} onClick={() => onSortierung([])}>
                {standard.length ? `Zurück zu: ${standardName}` : "Sortierung entfernen"}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </Ausklapp>
  );
}
